import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import * as signalR from '@microsoft/signalr';

import { isBearerAuthMode } from '../../utils/auth-mode';
import { getAuthToken } from '../../utils/auth-token';
import { getWsBaseUrl } from '../../utils/runtime-urls';

interface RequestWorkStatusChangeDto {
  eventName: 'RequestWorkStatusChange';
  title: string;
}

interface WebSocketContextType {
  connection: signalR.HubConnection | null;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);

  useEffect(() => {
    const connectionOptions: signalR.IHttpConnectionOptions = isBearerAuthMode()
      ? {
          accessTokenFactory: () => getAuthToken() ?? '',
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        }
      : {
          withCredentials: true,
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        };

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${getWsBaseUrl()}/signalhub`, connectionOptions)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    newConnection
      .start()
      .then(() => console.log('Connected to SignalR'))
      .catch((err) => console.error('Connection error:', err));

    newConnection.on('RequestWorkStatusChange', (data: RequestWorkStatusChangeDto) => {
      console.log('Received RequestWorkStatusChange event:', data);
      toast.info(data.title, {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    });

    newConnection.on('BlockEmployee', (data) => {
      console.log('BlockEmployee event:', data);
      window.location.hash = 'logout-auth';
    });

    setConnection(newConnection);

    return () => {
      newConnection.stop().then(() => console.log('Disconnected from SignalR'));
    };
  }, []);

  return <WebSocketContext.Provider value={{ connection }}>{children}</WebSocketContext.Provider>;
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
